import {formatDate} from '@angular/common';
import {
  AuditSafeComment,
  Case,
  IAuditSafeComment,
  IBusinessPartner,
  ICase,
  IDefect,
  IFlatRateUnit,
  IFlatRateUnitGroup,
  IJob,
  IPackage,
  IPart,
  IPicturePanel,
  IRepairOverview,
  IServiceContract,
  ISublet,
  ITechnicalDocument,
  IVehicle,
  Job,
  MileageDataSource
} from '@service-and-repairs/awpintegrationlib';
import {Customer, CustomerInfo, CustomerSource} from '@service-and-repairs/calm-leads-customer-lib-api';
import {Util} from '../../../util/util';
import {VehicleCampaignCollection} from '../../campaign/interfaces/vehicle-campaign-collection';
import {MileageRecord} from '../../mileage/interfaces/mileage-record';
import {VehicleServiceContract} from '../../service-contract/interfaces/vehicle-service-contract';
import {UserData} from '../../user/models/user-data';
import {VehicleWarning} from '../../vehicle-warning/interfaces/vehicle-warning';
import {WarrantyRestriction} from '../../warranty-restriction/interfaces/warranty-restriction';
import {JobProcessor} from './job-processor';

export class ServiceCase extends Case {
  private isAlreadyPersisted: boolean;
  saveEventIds: Set<string> = new Set<string>();

  private vehicleCampaignCollection: VehicleCampaignCollection;

  private serviceContracts: VehicleServiceContract[];

  private warrantyRestrictions: WarrantyRestriction[];

  private warnings: VehicleWarning[] = [];

  private mileageRecord: MileageRecord;
  private mileage: string;

  // Will be set if loading a service case fails.
  // Error will then be displayed as a tooltip in the respective tab.
  loadingError: string;

  // Don't make this constructor public. Use the static factory "fromPlainObject" instead.

  static constructMinimalServiceCase(user: UserData): ServiceCase {
    const businessPartner: IBusinessPartner = user.getBusinessPartner();
    const creationDate: Date = new Date();
    const contextPrefixedUserId: string = (user.getContext() + ':' + user.getId()).toLowerCase();

    const minimalServiceCase = new Case(
      Util.createRandomUuid(),
      formatDate(creationDate, 'short', user.getLocale()),
      creationDate,
      contextPrefixedUserId,
      user.getName(),
      creationDate,
      contextPrefixedUserId,
      user.getName()
    );
    minimalServiceCase.setAssignedBusinessPartnerId(businessPartner ? businessPartner.getBusinessPartnerId() : null);
    minimalServiceCase.setAssignedDistributionPartnerNumber(businessPartner ?
      businessPartner.getDistributionPartnerNumber() :
      null);
    minimalServiceCase.setAssignedOutletNumber(businessPartner ? businessPartner.getOutletNumber() : null);

    return ServiceCase.fromPlainObject({
      ...minimalServiceCase,
      isAlreadyPersisted: false
    });
  }

  /**
   * Creates a type safe ServiceCase object from data contained in a "plain object".
   *
   * Plain objects can occur, for example, when sending a type safe object to some other
   * component using window.postMessage(object). When receiving a plain object in this way,
   * it has no methods, just data.
   *
   * However, this factory method can also be used to easily create type safe
   * objects from JSON, e.g. to crate objects for unit testing.
   *
   * @param plainServiceCase
   */
  static fromPlainObject(plainServiceCase: any): ServiceCase {
    // Transform plain object into ICase
    const sourceCase: ICase = ServiceCase.constructFromPlainObject(plainServiceCase);

    // Transform ICase into ServiceCase
    const serviceCase = new ServiceCase(
      sourceCase.getExternalId(),
      sourceCase.getTitle(),
      sourceCase.getCreatedAt(),
      sourceCase.getCreatedByUserId(),
      sourceCase.getCreatedByUserName(),
      sourceCase.getModifiedAt(),
      sourceCase.getAssignedUserId(),
      sourceCase.getAssignedUserName(),
      sourceCase.getJobs(),
      sourceCase.getConsultationJobs()
    );
    serviceCase.setAssignedDistributionPartnerNumber(sourceCase.getAssignedDistributionPartnerNumber());
    serviceCase.setAssignedOutletNumber(sourceCase.getAssignedOutletNumber());
    serviceCase.setAssignedBusinessPartnerId(sourceCase.getAssignedBusinessPartnerId());
    serviceCase.setAssignedSpecialistGroup(sourceCase.getAssignedSpecialistGroup());
    serviceCase.setBrand(sourceCase.getBrand());
    serviceCase.setAuditSafeComments(sourceCase.getAuditSafeComments());
    serviceCase.setComment(sourceCase.getComment());
    serviceCase.setCreatedBySystem(sourceCase.getCreatedBySystem());
    serviceCase.setCurrencyCode(sourceCase.getCurrencyCode());
    serviceCase.setCustomers(sourceCase.getCustomers());
    serviceCase.setDevelopmentCode(sourceCase.getDevelopmentCode());
    serviceCase.setDmsOrderNumber(sourceCase.getDmsOrderNumber());
    serviceCase.setCustomerFirstName(sourceCase.getCustomerFirstName());
    serviceCase.setCustomerLastName(sourceCase.getCustomerLastName());
    serviceCase.setCustomerName(sourceCase.getCustomerName());
    serviceCase.setCustomerNameSource(sourceCase.getCustomerNameSource());
    serviceCase.setCustomerLanguage(sourceCase.getCustomerLanguage());
    serviceCase.setDmsCustomerNumber(sourceCase.getDmsCustomerNumber());
    serviceCase.setGcdmCustomerId(sourceCase.getGcdmCustomerId());
    serviceCase.setRetailCrmCustomerId(sourceCase.getRetailCrmCustomerId());
    serviceCase.setIsDmsPreOrder(sourceCase.getIsDmsPreOrder());
    serviceCase.setFulfillmentMode(sourceCase.getFulfillmentMode());
    serviceCase.setIsLocked(sourceCase.getIsLocked());
    serviceCase.setHasSalesStop(sourceCase.getHasSalesStop());
    serviceCase.setHasWarnings(sourceCase.getHasWarnings());
    serviceCase.setLicensePlate(sourceCase.getLicensePlate());
    serviceCase.setPaint(sourceCase.getPaint());
    serviceCase.setProductionDate(sourceCase.getProductionDate());
    serviceCase.setFirstRegistrationDate(sourceCase.getFirstRegistrationDate());
    serviceCase.setModelDesignation(sourceCase.getModelDesignation());
    serviceCase.setNetPrice(sourceCase.getNetPrice());
    serviceCase.setSchedulingInformation(sourceCase.getSchedulingInformation());
    serviceCase.setSmallCosyImageUrl(sourceCase.getSmallCosyImageUrl());
    serviceCase.setTotalPrice(sourceCase.getTotalPrice());
    serviceCase.setRetailPrice(sourceCase.getRetailPrice());
    serviceCase.setRetailSavingsReasons(sourceCase.getRetailSavingsReasons());
    serviceCase.setTypeCode(sourceCase.getTypeCode());
    serviceCase.setVehicle(sourceCase.getVehicle());
    serviceCase.setVinLong(sourceCase.getVinLong());
    serviceCase.setVinShort(sourceCase.getVinShort());
    serviceCase.setCurrentMileageInKilometers(sourceCase.getCurrentMileageInKilometers());
    serviceCase.setCurrentMileageSource(sourceCase.getCurrentMileageSource());
    serviceCase.setCurrentMileageRecordedAt(sourceCase.getCurrentMileageRecordedAt());
    serviceCase.setLastModifierUserName(sourceCase.getLastModifierUserName());
    serviceCase.setIsOnlineCheckInCompleted(sourceCase.getIsOnlineCheckInCompleted());
    serviceCase.setIsOnlinePaymentCompleted(sourceCase.getIsOnlinePaymentCompleted());
    serviceCase.setIsUpdatedByCustomer(sourceCase.getIsUpdatedByCustomer());
    serviceCase.setFinalSvcReportUrl(sourceCase.getFinalSvcReportUrl());
    serviceCase.setSvcSource(sourceCase.getSvcSource());
    serviceCase.setStatus(sourceCase.getStatus());
    serviceCase.setIsCreatedFromRoadsideAssistanceCase(sourceCase.getIsCreatedFromRoadsideAssistanceCase());
    serviceCase.setRoadsideAssistanceCase(sourceCase.getRoadsideAssistanceCase());

    // Set additional ServiceCase properties
    // default value is true even if property is null or undefined
    serviceCase.isAlreadyPersisted = plainServiceCase.isAlreadyPersisted !== false;
    serviceCase.setCampaigns(plainServiceCase.vehicleCampaignCollection);
    serviceCase.setServiceContracts(plainServiceCase.serviceContracts);
    serviceCase.setWarrantyRestrictions(plainServiceCase.warrantyRestrictions);
    serviceCase.setWarnings(plainServiceCase.warnings ?? []);
    serviceCase.mileageRecord = plainServiceCase.mileageRecord;
    serviceCase.mileage = plainServiceCase.mileage;

    serviceCase.updateDemandCategories();
    serviceCase.updateIsCostEstimateRequired();

    return serviceCase;
  }

  getAllJobs(): IJob[] {
    return this.getJobs().concat(this.getConsultationJobs());
  }

  addAuditSafeComment(comment: IAuditSafeComment): void {
    if (this.getExternalId() === comment.getParentItemId()) {
      // Service case
      this.getAuditSafeComments().push(comment);
      return;
    }

    // Jobs
    this.getAllJobs()
      .find((job: IJob): boolean => job.getExternalId() === comment.getParentItemId())
      ?.getAuditSafeComments()
      .push(comment);

    // Job content
    this.getAllJobs().forEach((job: IJob): void => {
      // Defects
      job.getDefects()
        .find((defect: IDefect): boolean => defect.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Flat rate units
      job.getFlatRateUnits()
        .find((flatRateUnit: IFlatRateUnit): boolean => flatRateUnit.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Parts
      job.getParts()
        .find((part: IPart): boolean => part.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Sublets
      job.getSublets()
        .find((sublet: ISublet): boolean => sublet.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Packages
      job.getPackages()
        .find((pack: IPackage): boolean => pack.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Flat rate unit groups
      job.getFlatRateUnitGroups()
        .find((group: IFlatRateUnitGroup): boolean => group.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Picture panels
      job.getPicturePanels()
        .find((panel: IPicturePanel): boolean => panel.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Repair overviews
      job.getRepairOverviews()
        .find((overview: IRepairOverview): boolean => overview.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Technical documents
      job.getTechnicalDocuments()
        .find((document: ITechnicalDocument): boolean => document.getExternalId() === comment.getParentItemId())
        ?.getAuditSafeComments()
        .push(comment);

      // Package content
      job.getPackages().forEach((pack: IPackage): void => {
        pack.getDefects()
          .find((defect: IDefect): boolean => defect.getExternalId() === comment.getParentItemId())
          ?.getAuditSafeComments()
          .push(comment);

        // Flat rate units
        pack.getFlatRateUnits()
          .find((flatRateUnit: IFlatRateUnit): boolean => flatRateUnit.getExternalId() === comment.getParentItemId())
          ?.getAuditSafeComments()
          .push(comment);

        // Parts
        pack.getParts()
          .find((part: IPart): boolean => part.getExternalId() === comment.getParentItemId())
          ?.getAuditSafeComments()
          .push(comment);

        // Sublets
        pack.getSublets()
          .find((sublet: ISublet): boolean => sublet.getExternalId() === comment.getParentItemId())
          ?.getAuditSafeComments()
          .push(comment);
      });
    });
  }

  /**
   * Jobs that are added from the potentials list, do not know the current service case. Their audit-safe comments
   * therefore still reference the service case they were created in. This assigns the current service case to every
   * such comment. Comments that already belong to this service case remain untouched. The reassigned comments are
   * returned so that they can be persisted separately, because audit-safe comments are stored independently of the
   * job in the backend.
   */
  assignAuditSafeComments(job: IJob): IAuditSafeComment[] {
    const reassignedComments: IAuditSafeComment[] = [];

    this.assignAuditSafeCommentsToItem(job.getAuditSafeComments(), reassignedComments);

    job.getDefects()
      .forEach((defect: IDefect): void =>
        this.assignAuditSafeCommentsToItem(defect.getAuditSafeComments(), reassignedComments));
    job.getFlatRateUnits().forEach((flatRateUnit: IFlatRateUnit): void =>
      this.assignAuditSafeCommentsToItem(flatRateUnit.getAuditSafeComments(), reassignedComments));
    job.getParts().forEach((part: IPart): void =>
      this.assignAuditSafeCommentsToItem(part.getAuditSafeComments(), reassignedComments));
    job.getSublets().forEach((sublet: ISublet): void =>
      this.assignAuditSafeCommentsToItem(sublet.getAuditSafeComments(), reassignedComments));
    job.getFlatRateUnitGroups().forEach((group: IFlatRateUnitGroup): void =>
      this.assignAuditSafeCommentsToItem(group.getAuditSafeComments(), reassignedComments));
    job.getPicturePanels().forEach((panel: IPicturePanel): void =>
      this.assignAuditSafeCommentsToItem(panel.getAuditSafeComments(), reassignedComments));
    job.getRepairOverviews().forEach((overview: IRepairOverview): void =>
      this.assignAuditSafeCommentsToItem(overview.getAuditSafeComments(), reassignedComments));
    job.getTechnicalDocuments().forEach((document: ITechnicalDocument): void =>
      this.assignAuditSafeCommentsToItem(document.getAuditSafeComments(), reassignedComments));

    job.getPackages().forEach((pack: IPackage): void => {
      this.assignAuditSafeCommentsToItem(pack.getAuditSafeComments(), reassignedComments);
      pack.getDefects().forEach((defect: IDefect): void =>
        this.assignAuditSafeCommentsToItem(defect.getAuditSafeComments(), reassignedComments));
      pack.getFlatRateUnits().forEach((flatRateUnit: IFlatRateUnit): void =>
        this.assignAuditSafeCommentsToItem(flatRateUnit.getAuditSafeComments(), reassignedComments));
      pack.getParts().forEach((part: IPart): void =>
        this.assignAuditSafeCommentsToItem(part.getAuditSafeComments(), reassignedComments));
      pack.getSublets().forEach((sublet: ISublet): void =>
        this.assignAuditSafeCommentsToItem(sublet.getAuditSafeComments(), reassignedComments));
    });

    return reassignedComments;
  }

  private assignAuditSafeCommentsToItem(comments: IAuditSafeComment[], reassignedComments: IAuditSafeComment[]): void {
    comments.forEach((comment: IAuditSafeComment, index: number): void => {
      if (comment.getParentServiceCaseId() !== this.getExternalId()) {
        comments[index] = AuditSafeComment.constructFromPlainObject({
          ...comment,
          parentServiceCaseId: this.getExternalId()
        });
        reassignedComments.push(comments[index]);
      }
    });
  }

  addDefects(defects: IDefect[]): IJob {
    const job: IJob = this.getActiveJobCreateIfNotExists(defects[0].getDescription());
    JobProcessor.addDefects(job, defects);
    return job;
  }

  addFlatRateUnits(flatRateUnits: IFlatRateUnit[]): IJob {
    const job: IJob = this.getActiveJobCreateIfNotExists(flatRateUnits[0].getDescription());
    JobProcessor.addFlatRateUnits(job, flatRateUnits);
    return job;
  }

  addFlatRateUnitsToJob(jobId: string, flatRateUnits: IFlatRateUnit[]): IJob | null {
    const job: IJob = this.getJobByExternalId(jobId);
    if (!job) {
      return null;
    }
    JobProcessor.addFlatRateUnits(job, flatRateUnits);
    return job;
  }

  addParts(parts: IPart[]): IJob {
    const job: IJob = this.getActiveJobCreateIfNotExists(parts[0].getDescription());
    JobProcessor.addParts(job, parts);
    return job;
  }

  addPartsToJob(jobId: string, parts: IPart[]): IJob | null {
    const job: IJob = this.getJobByExternalId(jobId);
    if (!job) {
      return null;
    }
    JobProcessor.addParts(job, parts);
    return job;
  }

  addFlatRateUnitGroup(flatRateUnitGroup: IFlatRateUnitGroup): IJob {
    const newJobTitle: string = flatRateUnitGroup.getFlatRateUnits()
      .find((flatRateUnit: IFlatRateUnit): boolean => flatRateUnit.getNumber() === flatRateUnitGroup.getNumber())
      ?.getDescription();

    const job: IJob = this.getActiveJobCreateIfNotExists(newJobTitle);
    JobProcessor.addFlatRateUnitGroup(job, flatRateUnitGroup);
    return job;
  }

  addPicturePanel(picturePanel: IPicturePanel, defaultNewJobTitle: string): IJob {
    const job: IJob =
      this.getActiveJobCreateIfNotExists(picturePanel.getParts().at(0)?.getDescription() || defaultNewJobTitle);
    JobProcessor.addPicturePanel(job, picturePanel);
    return job;
  }

  addRepairOverview(repairOverview: IRepairOverview): IJob {
    const job: IJob = this.getActiveJobCreateIfNotExists(repairOverview.getTitle());
    JobProcessor.addRepairOverview(job, repairOverview);
    return job;
  }

  addTechnicalDocument(technicalDocument: ITechnicalDocument): IJob {
    const job: IJob = this.getActiveJobCreateIfNotExists(technicalDocument.getTitle());
    JobProcessor.addTechnicalDocument(job, technicalDocument);
    return job;
  }

  addJob(job: IJob, toConsultationList: boolean): IJob {
    // If job has an existing service contract, update it instead adding it again
    const optionalServiceContract: IServiceContract = job.getServiceContract();
    if (optionalServiceContract && this.hasServiceContract(optionalServiceContract)) {
      return this.replaceExistingServiceContract(optionalServiceContract);
    }

    // If job has an existing service package, update it instead adding it again
    const optionalPackage: IPackage = job.getPackages().at(0);
    if (optionalPackage && this.hasPackage(optionalPackage) && !optionalPackage.getIsLocal()) {
      return this.replaceExistingPackage(optionalPackage);
    }

    // Job has no existing service contract and no existing service package, content does not need special handling and
    // can be added as is
    if (toConsultationList) {
      this.addOrReplaceJob(job, this.getConsultationJobs());
    } else {
      this.addOrReplaceJob(job, this.getJobs());
    }
    return job;
  }

  addOrReplacePackage(servicePackage: IPackage): IJob {
    if (this.hasPackage(servicePackage) && !servicePackage.getIsLocal()) {
      return this.replaceExistingPackage(servicePackage);
    } else {
      return this.addPackageToNewJobInBasket(servicePackage);
    }
  }

  private replaceExistingPackage(servicePackage: IPackage): IJob {
    const job: IJob = this.getAllJobs().find((job: IJob): boolean => JobProcessor.hasPackage(job, servicePackage));
    JobProcessor.addOrReplacePackage(job, servicePackage);

    return job;
  }

  private addPackageToNewJobInBasket(servicePackage: IPackage): IJob {
    const job: IJob = new Job(Util.createRandomUuid(), servicePackage.getDescription());
    job.setPositionNumber(undefined); // keep position numbers and order of existing jobs, sort job at the end
    JobProcessor.addOrReplacePackage(job, servicePackage);
    this.addOrReplaceJob(job, this.getJobs());
    return job;
  }

  private hasPackage(servicePackage: IPackage): boolean {
    return this.getAllJobs().some((job: IJob): boolean => JobProcessor.hasPackage(job, servicePackage));
  }

  addOrReplaceServiceContract(serviceContract: IServiceContract): IJob {
    if (this.hasServiceContract(serviceContract)) {
      return this.replaceExistingServiceContract(serviceContract);
    } else {
      return this.addServiceContractToNewJobInBasket(serviceContract);
    }
  }

  private replaceExistingServiceContract(serviceContract: IServiceContract): IJob {
    const job: IJob = this.getAllJobs().find((j: IJob): boolean => JobProcessor.hasServiceContract(
      j,
      serviceContract
    ));
    JobProcessor.addOrReplaceServiceContract(job, serviceContract);

    return job;
  }

  private addServiceContractToNewJobInBasket(serviceContract: IServiceContract): IJob {
    const job: IJob = new Job(Util.createRandomUuid(), serviceContract.getShortDescription());
    JobProcessor.addOrReplaceServiceContract(job, serviceContract);
    this.addOrReplaceJob(job, this.getJobs());
    return job;
  }

  private hasServiceContract(serviceContract: any): boolean {
    return this.getAllJobs().some((job: IJob): boolean => JobProcessor.hasServiceContract(job, serviceContract));
  }

  vehicleDataMissing(): boolean {
    // check if vehicle data is missing, best guess is there is a VIN7 but no model designation
    return this.getVinShort() && !this.getModelDesignation();
  }

  getCampaigns(): VehicleCampaignCollection {
    return this.vehicleCampaignCollection;
  }

  getServiceContracts(): VehicleServiceContract[] {
    return this.serviceContracts;
  }

  getWarnings(): VehicleWarning[] {
    return this.warnings;
  }

  setWarnings(warnings: VehicleWarning[]): void {
    this.warnings = warnings;
  }

  addWarning(warning: VehicleWarning): void {
    this.warnings.push(warning);
    this.setHasWarnings(true);
    if (warning.isStop) {
      this.setHasSalesStop(true);
    }
  }

  setWarrantyRestrictions(warrantyRestrictions: WarrantyRestriction[]): void {
    this.warrantyRestrictions = warrantyRestrictions;
  }

  getWarrantyRestrictions(): WarrantyRestriction[] {
    return this.warrantyRestrictions;
  }

  /**
   * Applies the metadata of a service case published by another AWP app.
   *
   * The lock state is deliberately not copied: it is owned by the backend and only arrives when the service case is
   * loaded or reloaded. Accepting it from a published service case would let another app lock or unlock the local
   * copy, which would either make an editable service case read-only or make a locked one editable while the backend
   * keeps rejecting every write.
   *
   * @param update The service case to take the metadata from.
   */
  updateMetaDataOnly(update: ServiceCase): void {
    this.setAssignedDistributionPartnerNumber(update.getAssignedDistributionPartnerNumber());
    this.setAssignedOutletNumber(update.getAssignedOutletNumber());
    this.setAssignedBusinessPartnerId(update.getAssignedBusinessPartnerId());
    this.setAssignedUserId(update.getAssignedUserId());
    this.setAssignedUserName(update.getAssignedUserName());
    this.setAssignedSpecialistGroup(update.getAssignedSpecialistGroup());
    this.setAuditSafeComments(update.getAuditSafeComments());
    this.setComment(update.getComment());
    this.setCustomers(update.getCustomers());
    this.setCurrencyCode(update.getCurrencyCode());
    this.setDmsOrderNumber(update.getDmsOrderNumber());
    this.setCustomerFirstName(update.getCustomerFirstName());
    this.setCustomerLastName(update.getCustomerLastName());
    this.setCustomerName(update.getCustomerName());
    this.setCustomerNameSource(update.getCustomerNameSource());
    this.setCustomerLanguage(update.getCustomerLanguage());
    this.setDmsCustomerNumber(update.getDmsCustomerNumber());
    this.setGcdmCustomerId(update.getGcdmCustomerId());
    this.setRetailCrmCustomerId(update.getRetailCrmCustomerId());
    this.setIsDmsPreOrder(update.getIsDmsPreOrder());
    // Set once: the first successful DMS transfer fixes the view, later updates must not change it
    if (!this.getFulfillmentMode()) {
      this.setFulfillmentMode(update.getFulfillmentMode());
    }
    this.setModifiedAt(update.getModifiedAt());
    this.setTitle(update.getTitle());
    this.setNetPrice(update.getNetPrice());
    this.setSchedulingInformation(update.getSchedulingInformation());
    this.setTotalPrice(update.getTotalPrice());
    this.setRetailPrice(update.getRetailPrice());
    this.setRetailSavingsReasons(update.getRetailSavingsReasons());
    this.setLastModifierUserName(update.getLastModifierUserName());
    this.setIsOnlineCheckInCompleted(update.getIsOnlineCheckInCompleted());
    this.setIsOnlinePaymentCompleted(update.getIsOnlinePaymentCompleted());
    this.setIsUpdatedByCustomer(update.getIsUpdatedByCustomer());
    this.setFinalSvcReportUrl(update.getFinalSvcReportUrl());
    this.setSvcSource(update.getSvcSource());
    this.setStatus(update.getStatus());
    this.updateVehicle(update.getVehicle());
    this.setPaint(update.getPaint());
    this.setProductionDate(update.getProductionDate());
    this.setFirstRegistrationDate(update.getFirstRegistrationDate());
    this.setCurrentMileageInKilometers(update.getCurrentMileageInKilometers());
    this.setCurrentMileageSource(update.getCurrentMileageSource());
    this.setCurrentMileageRecordedAt(update.getCurrentMileageRecordedAt());
    // do not update mileage record and mileage string as this is not know in a lib Service Case
    this.setWarnings(update.getWarnings());
    // do not update demand categories and cost estimate required as this is only necessary, if jobs have changed
  }

  updateJob(updatedJob: IJob): IJob {
    const existingJob: IJob = this.getJobByExternalId(updatedJob.getExternalId());
    if (existingJob) {
      if (!existingJob.getIsActive() && updatedJob.getIsActive()) {
        // if job shall be activated, all other jobs need to be deactivated
        this.getAllJobs().forEach((job: IJob) => job.setIsActive(false));
      }
      existingJob.setIsActive(updatedJob.getIsActive());
      existingJob.setDemandCategory(updatedJob.getDemandCategory().trim());
      existingJob.setDemandIds(updatedJob.getDemandIds());
      existingJob.setCbsIds(updatedJob.getCbsIds());
      existingJob.setCcmIds(updatedJob.getCcmIds());
      existingJob.setTechnicalCampaignNumbers(updatedJob.getTechnicalCampaignNumbers());
      existingJob.setPositionNumber(updatedJob.getPositionNumber());
      existingJob.setTitle(updatedJob.getTitle());
      existingJob.setAuditSafeComments(updatedJob.getAuditSafeComments());
      existingJob.setComment(updatedJob.getComment());
      existingJob.setCustomerStatement(updatedJob.getCustomerStatement());
      JobProcessor.setExternalIdsIfMissing(updatedJob);
      existingJob.setDefects(updatedJob.getDefects());
      existingJob.setFlatRateUnits(updatedJob.getFlatRateUnits());
      existingJob.setPackages(updatedJob.getPackages());
      existingJob.setParts(updatedJob.getParts());
      existingJob.setSublets(updatedJob.getSublets());
      existingJob.setFlatRateUnitGroups(updatedJob.getFlatRateUnitGroups());
      existingJob.setPicturePanels(updatedJob.getPicturePanels());
      existingJob.setRepairOverviews(updatedJob.getRepairOverviews());
      existingJob.setTechnicalDocuments(updatedJob.getTechnicalDocuments());
      existingJob.setIsSelected(updatedJob.getIsSelected());
      existingJob.setIsFrozenAfterDmsTransfer(updatedJob.getIsFrozenAfterDmsTransfer());
      existingJob.setCustomerResponse(updatedJob.getCustomerResponse());
      existingJob.setIsRepeatVisit(updatedJob.getIsRepeatVisit());
      existingJob.setIsBreakdownCase(updatedJob.getIsBreakdownCase());
      existingJob.setIsCostEstimateRequired(updatedJob.getIsCostEstimateRequired());
      if (updatedJob.getCostCenter()?.trim()) {
        existingJob.setCostCenter(updatedJob.getCostCenter());
        existingJob.setCostCenterAccount(updatedJob.getCostCenterAccount());
        existingJob.setCostCenterDescription(updatedJob.getCostCenterDescription());
        existingJob.setIsCostCenterManuallySelected(updatedJob.getIsCostCenterManuallySelected());
      }

      this.updateDemandCategories();
      this.updateIsCostEstimateRequired();
    }

    return existingJob;
  }

  updateVehicle(updatedVehicle: IVehicle): void {
    this.setVehicle(updatedVehicle);
    this.setBrand(updatedVehicle?.getBrand() || null);
    this.setVinLong(updatedVehicle?.getVin() || null);
    this.setVinShort(updatedVehicle?.getVin7() || null);
    this.setLicensePlate(updatedVehicle?.getLicensePlate() || null);
    this.setPaint(updatedVehicle?.getPaint() || null);
    this.setProductionDate(this.formatVehicleDate(updatedVehicle?.getProductionDate()));
    this.setFirstRegistrationDate(this.formatVehicleDate(updatedVehicle?.getFirstRegistrationDate()));
    this.setTypeCode(updatedVehicle?.getTypeCode() || null);
    this.setModelDesignation(updatedVehicle?.getModelDesignation() || null);
    this.setDevelopmentCode(updatedVehicle?.getDevelopmentSeries() || null);
    this.setHasWarnings(false);
    this.setHasSalesStop(false);
    this.setSmallCosyImageUrl(updatedVehicle?.getImageUrlSmall() || null);
    this.setWarnings([]);
  }

  private formatVehicleDate(vehicleDate: Date | null | undefined): string | null {
    return vehicleDate ? formatDate(vehicleDate, 'yyyy-MM-dd', 'en-US', 'UTC') : null;
  }

  setCurrentMileage(mileageRecord: MileageRecord, mileageUnit: string, locale: string): boolean {
    const currentMileageBefore: number = this.getCurrentMileageInKilometers();
    const currentMileageSourceBefore: MileageDataSource = this.getCurrentMileageSource();
    const currentMileageRecordedAtBefore: Date = this.getCurrentMileageRecordedAt();

    this.mileageRecord = mileageRecord;
    // The mileage is null, if mileageRecord is null. However, mileage is undefined, if mileageRecord is undefined.
    this.setCurrentMileageInKilometers(mileageRecord !== null ? mileageRecord?.value : null);
    this.setCurrentMileageSource(mileageRecord?.source || null); // source is never undefined
    this.setCurrentMileageRecordedAt(mileageRecord?.recordedAt ? new Date(mileageRecord.recordedAt) : null);
    this.applyUserData(mileageUnit, locale);

    return currentMileageBefore !== this.getCurrentMileageInKilometers()
      || currentMileageSourceBefore !== this.getCurrentMileageSource()
      || currentMileageRecordedAtBefore?.getTime() !== this.getCurrentMileageRecordedAt()?.getTime();
  }

  applyUserData(mileageUnit: string, locale: string): void {
    const mileage: number = this.getCurrentMileageInKilometers();
    if (mileage === undefined) {
      this.mileage = '- ' + mileageUnit;
    } else {
      const currentMileage = (mileageUnit === 'km' ? mileage : mileage / 1.609344);
      this.mileage = Math.round(currentMileage).toLocaleString(locale) + ' ' + mileageUnit;
    }
  }

  getMileageRecord(): MileageRecord {
    return this.mileageRecord;
  }

  getMileage(): string {
    return this.mileage;
  }

  setCampaigns(vehicleCampaignCollection: VehicleCampaignCollection): void {
    this.vehicleCampaignCollection = vehicleCampaignCollection;
  }

  setServiceContracts(serviceContracts: VehicleServiceContract[]): void {
    this.serviceContracts = serviceContracts;
  }

  private getActiveJobCreateIfNotExists(title: string): IJob {
    const job: IJob = this.getActiveJob() ?? this.addEmptyJobAsFirstJobInBasket(title);
    if (JobProcessor.isEmpty(job)) {
      job.setTitle(title);
    }
    return job;
  }

  getActiveJob(): IJob | null {
    const activeJob: IJob = this.getAllJobs().find((job: IJob) => job.getIsActive());
    if (!activeJob) {
      console.log(
        '[Webapp] ServiceCase {} has no active job. This is fine in some cases, but unusual.',
        this.getExternalId()
      );
    }
    return activeJob || null;
  }

  addEmptyJobAsFirstJobInBasket(title: string): IJob {
    const emptyJob: IJob = new Job(Util.createRandomUuid(), title);
    emptyJob.setIsSelected(true);
    emptyJob.setPositionNumber(-1); // Ensure the new job is still the first after sorting
    this.getJobs().unshift(emptyJob);
    this.markJobAsActive(emptyJob);
    this.setJobPositionNumbers();
    this.updateDemandCategories(); // default demand category may not have been in the job list
    // no need to update cost estimate required, as empty job does not require a cost estimate
    return emptyJob;
  }

  addEmptyJobAsFirstJobInConsultationList(title: string): IJob {
    const emptyJob: IJob = new Job(Util.createRandomUuid(), title);
    emptyJob.setIsSelected(true);
    emptyJob.setPositionNumber(-1); // Ensure the new job is still the first after sorting
    this.getConsultationJobs().unshift(emptyJob);
    this.markJobAsActive(emptyJob);
    this.setJobPositionNumbers();
    return emptyJob;
  }

  markJobAsActive(job: IJob): void {
    this.getAllJobs().forEach((j: IJob): void => j.setIsActive(false));
    job.setIsActive(true);
  }

  private addOrReplaceJob(jobToAdd: IJob, jobs: IJob[]): void {
    JobProcessor.prepareJob(jobToAdd);

    const index: number = this.getJobIndex(jobToAdd, jobs);
    if (index >= 0) {
      jobToAdd.setPositionNumber(jobs[index].getPositionNumber()); // keep position number and job order
      jobs[index] = jobToAdd;
    } else {
      jobToAdd.setPositionNumber(undefined); // keep position numbers and order of existing jobs, sort job at the end
      jobs.push(jobToAdd);
    }
    if (jobToAdd.getPackages().length === 0) {
      this.markJobAsActive(jobToAdd);
    }
    this.updateDemandCategories();
    this.updateIsCostEstimateRequired();
    this.setJobPositionNumbers();
  }

  private getJobIndex(job: IJob, jobs: IJob[]): number {
    return jobs.findIndex((j: IJob): boolean => j.getExternalId() === job.getExternalId());
  }

  getJobByExternalId(externalId: string): IJob {
    return this.getAllJobs().find((job: IJob): boolean => job.getExternalId() === externalId);
  }

  /**
   * Removes a job from this service case.
   * @param jobToRemove job to remove.
   */
  removeJob(jobToRemove: IJob): IJob {
    // Remove jobs
    const existingIndexBasket: number = this.getJobIndex(jobToRemove, this.getJobs());
    if (existingIndexBasket >= 0) {
      this.getJobs().splice(existingIndexBasket, 1);
    }
    const existingIndexConsultationList: number = this.getJobIndex(jobToRemove, this.getConsultationJobs());
    if (existingIndexConsultationList >= 0) {
      this.getConsultationJobs().splice(existingIndexConsultationList, 1);
    }

    this.updateDemandCategories();
    this.updateIsCostEstimateRequired();

    // Sort and reassign position numbers
    this.setJobPositionNumbers();

    return jobToRemove;
  }

  /**
   * Get the number of elements in the service case.
   * Parts will be counted by their quantity, a package counts as one element.
   */
  getNumberOfElementsInBasket(): number {
    let numberOfElements: number = 0;
    this.getAllJobs().forEach((job: IJob): void => {
      numberOfElements += job.getParts().reduce((sum: number, part: IPart): number => sum + part.getQuantity(), 0);
      numberOfElements += job.getFlatRateUnits().length;
      numberOfElements += job.getPackages().length;
      numberOfElements += job.getDefects().length;
      numberOfElements += job.getSublets().length;
      numberOfElements += job.getFlatRateUnitGroups().length;
      numberOfElements += job.getPicturePanels().length;
      numberOfElements += job.getRepairOverviews().length;
      numberOfElements += job.getTechnicalDocuments().length;
      numberOfElements += job.getServiceContract() ? 1 : 0;
    });
    return numberOfElements;
  }

  private setJobPositionNumbers(): void {
    this.sortJobsByPositionNumber();
    for (let i: number = 0; i < this.getJobs().length; i++) {
      this.getJobs()[i].setPositionNumber(i + 1);
    }
    for (let i: number = 0; i < this.getConsultationJobs().length; i++) {
      this.getConsultationJobs()[i].setPositionNumber(i + 1);
    }
  }

  private sortJobsByPositionNumber(): void {
    this.getJobs().sort((a, b) => {
      // Arrange jobs with no position number to the end of the list
      return !a.getPositionNumber() || (a.getPositionNumber() > b.getPositionNumber()) ? 1 : -1;
    });
    this.getConsultationJobs().sort((a, b) => {
      // Arrange jobs with no position number to the end of the list
      return !a.getPositionNumber() || (a.getPositionNumber() > b.getPositionNumber()) ? 1 : -1;
    });
  }

  /**
   * Returns true, if the service case was persisted on backend side, false otherwise.
   */
  isPersisted(): boolean {
    return this.isAlreadyPersisted;
  }

  updateCustomers(customers: CustomerInfo): boolean {
    this.setCustomers(customers);
    return this.setPreferredCustomer(customers.customers);
  }

  /**
   * Sets the preferred customer's IDs and name on this service case.
   *
   * Selects the preferred customer from the list (first one marked as preferred, or the first customer if none).
   * Updates DMS customer number, Retail CRM customer ID, and GCDM customer ID if they differ.
   * Only updates the customer name if the new customer's source has higher priority than the current one
   * (priority order: preferred > DMS > RETAIL_CRM > WHOLESALE_CRM > GCDM > DDR).
   *
   * @param customers Array of customers to select the preferred customer from
   * @returns true if any customer data was changed, false otherwise
   */
  setPreferredCustomer(customers: Customer[]): boolean {
    const preferredCustomer: Customer = customers?.find((customer: Customer): boolean => customer.isPreferred)
      ?? customers?.find((customer: Customer): boolean => !!customer);

    let changed: boolean = false;

    // IDs will always be set, if the ID is changed
    if (preferredCustomer?.idCollection?.dmsId
      && this.getDmsCustomerNumber() !== preferredCustomer.idCollection.dmsId) {
      this.setDmsCustomerNumber(preferredCustomer.idCollection.dmsId);
      changed = true;
    }
    if (preferredCustomer?.idCollection?.retailCrmOkuNumber
      && this.getRetailCrmCustomerId() !== preferredCustomer.idCollection.retailCrmOkuNumber) {
      this.setRetailCrmCustomerId(preferredCustomer.idCollection.retailCrmOkuNumber);
      changed = true;
    }
    if (preferredCustomer?.idCollection?.gcid && this.getGcdmCustomerId() !== preferredCustomer.idCollection.gcid) {
      this.setGcdmCustomerId(preferredCustomer.idCollection.gcid);
      changed = true;
    } else if (this.getGcdmCustomerId() === undefined) {
      this.setGcdmCustomerId(null);
      changed = true;
    }

    // Customer name (and its source) will only set, if the determined preferred customer is not of a lower ranked
    // source: 1. marked as preferred 2. from DMS 3. from RETAIL CRM 4. from WHOLESALE_CRM 5. from GCDM 6. from DDR.
    // Without any customer (e.g. empty customer list), the current customer name is kept.
    if (preferredCustomer
      && this.isCustomerSourceHigher(preferredCustomer)
      && this.hasCustomerNameChanged(preferredCustomer)) {
      this.updateCustomerName(preferredCustomer.nameCollection);
      this.setCustomerNameSource(preferredCustomer.source);
      changed = true;
    }

    return changed;
  }

  /**
   * Replaces customer name with first and last name, if at least one value is present. Keeps customer name, if both
   * are missing. This is necessary, because some customer sources only provide full name.
   */
  updateCustomerName(nameCollection: { firstName?: string, lastName?: string, name?: string }): void {
    const firstName: string = nameCollection?.firstName?.trim() ?? '';
    const lastName: string = nameCollection?.lastName?.trim() ?? '';
    const fullName: string = (firstName + ' ' + lastName).trim();
    // use undefined if empty or missing (||-operator is also false for empty strings)
    this.setCustomerFirstName(firstName || undefined);
    this.setCustomerLastName(lastName || undefined);
    // use full name from source if first and last name are empty or missing
    this.setCustomerName(fullName || nameCollection?.name);
  }

  private isCustomerSourceHigher(customer: Customer): boolean {
    const rankedCustomerSources = [
      CustomerSource.DDR.toString(),
      CustomerSource.GCDM.toString(),
      CustomerSource.WHOLESALE_CRM.toString(),
      CustomerSource.RETAIL_CRM.toString(),
      CustomerSource.DMS.toString()
    ];
    return customer?.isPreferred
      || rankedCustomerSources.indexOf(customer?.source) >= rankedCustomerSources.indexOf(this.getCustomerNameSource());
  }

  private hasCustomerNameChanged(customer: Customer): boolean {
    return this.getCustomerName() !== customer?.nameCollection?.name
      || this.getCustomerFirstName() !== customer?.nameCollection?.firstName
      || this.getCustomerLastName() !== customer?.nameCollection?.lastName
      || this.getCustomerNameSource() !== customer?.source;
  }

  private updateDemandCategories(): void {
    this.setDemandCategories(
      this.getJobs()
        .map(job => job.getDemandCategory().trim())
        .filter((value, index, array) => array.indexOf(value) === index)
        .filter(category => category.length > 0)
        .sort((a, b) => a.localeCompare(b))
        .join(',')
    );
  }

  private updateIsCostEstimateRequired(): void {
    this.setIsCostEstimateRequired(this.getJobs().some(job => job.getIsCostEstimateRequired()));
  }
}
